import { useSyncExternalStore } from 'react';

import { jwtDecode } from 'jwt-decode';

import {
  CACHED_TOKEN_STORAGE_ATTRIBUTE_NAME,
  GX_TOKEN_STORAGE_ATTRIBUTE_NAME,
  IMPERSONATOR_STORAGE_ATTRIBUTE_NAME,
  PATHNAME_STORAGE_ATTRIBUTE_NAME,
  TRIP_TOKEN_STORAGE_ATTRIBUTE_NAME
} from '@/constants/constants';

const STORAGE_EVENT = 'token_storage_event';
const GX_TOKEN_EXPIRY_KEY = 'gxTokenExpiry';
const tokenExpiryPeriod = import.meta.env.VITE_TOKEN_EXPIRY_PERIOD;

const useStorage = (item: string, storage: Storage) =>
  useSyncExternalStore(
    onStoreChange => {
      window.addEventListener('storage', onStoreChange);
      window.addEventListener(STORAGE_EVENT, onStoreChange);
      return () => {
        window.removeEventListener('storage', onStoreChange);
        window.removeEventListener(STORAGE_EVENT, onStoreChange);
      };
    },
    () => storage.getItem(item)
  );

export const useTripTokenFromStorage = () =>
  useStorage(TRIP_TOKEN_STORAGE_ATTRIBUTE_NAME, sessionStorage);

export const getTripTokenFromStorage = () =>
  sessionStorage.getItem(TRIP_TOKEN_STORAGE_ATTRIBUTE_NAME);
export const setTripTokenToStorage = (tripToken: string): void => {
  sessionStorage.setItem(TRIP_TOKEN_STORAGE_ATTRIBUTE_NAME, tripToken);
  window.dispatchEvent(new Event(STORAGE_EVENT));
};

export const useGxTokenFromStorage = () =>
  useStorage(GX_TOKEN_STORAGE_ATTRIBUTE_NAME, localStorage);

export const useGxTokenExpiryFromStorage = () => useStorage(GX_TOKEN_EXPIRY_KEY, localStorage);
export const getGxTokenFromStorage = () => localStorage.getItem(GX_TOKEN_STORAGE_ATTRIBUTE_NAME);
export const useImpersonatorEmailFromStorage = () =>
  useStorage(IMPERSONATOR_STORAGE_ATTRIBUTE_NAME, localStorage);
function setGxTokenExpiryToStorage(gxToken: string) {
  let expiryTime = 0;
  const expirationOfToken = jwtDecode(gxToken).exp;
  if (expirationOfToken) {
    expiryTime = expirationOfToken - tokenExpiryPeriod;
  }
  localStorage.setItem(GX_TOKEN_EXPIRY_KEY, expiryTime.toString());
  return expiryTime.toString();
}

export const setGxTokenToStorage = (gxToken: string): void => {
  localStorage.setItem(GX_TOKEN_STORAGE_ATTRIBUTE_NAME, gxToken);
  setGxTokenExpiryToStorage(gxToken);
  window.dispatchEvent(new Event(STORAGE_EVENT));
};

export const setImpostorTokenToStorage = (
  impostorToken: string,
  impersonatorEmail: string
): void => {
  const gxToken = localStorage.getItem(GX_TOKEN_STORAGE_ATTRIBUTE_NAME);
  const cachedToken = localStorage.getItem(CACHED_TOKEN_STORAGE_ATTRIBUTE_NAME);
  if (gxToken) {
    if (cachedToken) {
      localStorage.setItem(GX_TOKEN_STORAGE_ATTRIBUTE_NAME, impostorToken);
    } else {
      localStorage.setItem(CACHED_TOKEN_STORAGE_ATTRIBUTE_NAME, gxToken);
      localStorage.setItem(GX_TOKEN_STORAGE_ATTRIBUTE_NAME, impostorToken);
    }
    localStorage.setItem(IMPERSONATOR_STORAGE_ATTRIBUTE_NAME, impersonatorEmail);
    sessionStorage.removeItem(TRIP_TOKEN_STORAGE_ATTRIBUTE_NAME);
    window.dispatchEvent(new Event(STORAGE_EVENT));
  }
};
export const clearImpostorTokenFromStorage = (): void => {
  const gxToken = localStorage.getItem(GX_TOKEN_STORAGE_ATTRIBUTE_NAME);
  const cachedToken = localStorage.getItem(CACHED_TOKEN_STORAGE_ATTRIBUTE_NAME);

  if (gxToken && cachedToken) {
    localStorage.setItem(GX_TOKEN_STORAGE_ATTRIBUTE_NAME, cachedToken);

    sessionStorage.removeItem(TRIP_TOKEN_STORAGE_ATTRIBUTE_NAME);
    localStorage.removeItem(CACHED_TOKEN_STORAGE_ATTRIBUTE_NAME);
    localStorage.removeItem(IMPERSONATOR_STORAGE_ATTRIBUTE_NAME);
  }
  window.dispatchEvent(new Event(STORAGE_EVENT));
};

export const setPathnameToStorage = (pathname: string): void => {
  localStorage.setItem(PATHNAME_STORAGE_ATTRIBUTE_NAME, pathname);
  window.dispatchEvent(new Event(STORAGE_EVENT));
};

export const usePathnameFromStorage = () =>
  useStorage(PATHNAME_STORAGE_ATTRIBUTE_NAME, localStorage);

export const clearPathnameFromStorage = () => {
  localStorage.removeItem(PATHNAME_STORAGE_ATTRIBUTE_NAME);
};

export const clearStoragesFromTokensAndUserInfo = () => {
  sessionStorage.removeItem(TRIP_TOKEN_STORAGE_ATTRIBUTE_NAME);
  localStorage.removeItem(GX_TOKEN_STORAGE_ATTRIBUTE_NAME);
  localStorage.removeItem(GX_TOKEN_EXPIRY_KEY);
  localStorage.removeItem(CACHED_TOKEN_STORAGE_ATTRIBUTE_NAME);
  localStorage.removeItem(IMPERSONATOR_STORAGE_ATTRIBUTE_NAME);
  window.dispatchEvent(new Event(STORAGE_EVENT));
};
